import React, { useState, useEffect, useMemo } from "react";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaCalendarCheck,
  FaUser,
  FaHeart,
  FaRegHeart,
} from "react-icons/fa";
import Button from "./../../../components/Button/Button";
import {
  getAdherentsCountByEvent,
  getAdherentsByEvent,
  registerAdherent,
  unregisterAdherent,
} from "./../../../services/eventServices";
import {
  getAdherentsByUser,
  getAllAdherentsList,
} from "./../../../services/adherentServices";
import { sendNotification } from "./../../../services/notificationServices";
import { getAdminsAndInstructorsIds } from "./../../../services/userServices";
import {
  addUserEvent,
  deleteUserEvent,
  getUsersEvents,
} from "./../../../services/usersEventsServices";
import EventDetailsModalEnseignants from "./../../PagesEnseignants/EventsPage/EventDetailsModalEnseignants";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import { ToastContainer, toast } from "react-toastify";

// ✅ helper pour absolutiser /uploads/…
import { toAbsoluteEventPhotoUrl } from "../../../services/eventPhotoServices";

const EventCardUser = ({ event, userId }) => {
  const [adherentsCount, setAdherentsCount] = useState(0);
  const [adherents, setAdherents] = useState([]);
  const [userAdherents, setUserAdherents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  // panneau interne
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState("register");
  const [selectedAdherent, setSelectedAdherent] = useState("");

  // 🖼️ preview plein écran (si tu l’utilises ailleurs)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isAdminOrInstructor = [1, 3].includes(user?.role_id);

  const isEventPast = new Date(event.end || event.start) < new Date();
  const eventBgClass = isEventPast ? "bg-slate-300" : "bg-slate-100";

  // ✅ Statut inscriptions (par défaut: autorisé)
  const canRegister = !!Number(event?.can_register ?? 1);

  // URL image (URL absolue, /uploads, data:, blob:, etc.)
  const imgSrc = useMemo(
    () => toAbsoluteEventPhotoUrl(event?.photo_url) || "/img/no-image.jpg",
    [event?.photo_url]
  );

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [countData, eventAdh, myAdh] = await Promise.all([
          getAdherentsCountByEvent(event.id),
          getAdherentsByEvent(event.id),
          isAdminOrInstructor
            ? getAllAdherentsList()
            : getAdherentsByUser(user.id),
        ]);
        setAdherentsCount(countData);
        setAdherents(eventAdh);
        setUserAdherents(myAdh);

        const favorites = await getUsersEvents(userId);
        setIsFavorite(favorites.some((fav) => fav.event_id === event.id));
      } catch (e) {
        console.error("Error loading event data:", e);
      }
    };
    loadData();
  }, [event.id, user.id, isAdminOrInstructor, userId]);

  // --- Helpers
  const userRegisteredAdherents = useMemo(
    () =>
      adherents.filter((adh) =>
        userAdherents.some((u) => u.id === adh.id && adh.AdherentEvent)
      ),
    [adherents, userAdherents]
  );

  const eligibleForUnregister = useMemo(() => {
    const list = isAdminOrInstructor
      ? adherents
      : adherents.filter((adh) =>
          userAdherents.map((u) => u.id).includes(adh.id)
        );
    return [...list].sort((a, b) =>
      `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, "fr")
    );
  }, [adherents, isAdminOrInstructor, userAdherents]);

  const eligibleForRegister = useMemo(() => {
    const alreadyIds = new Set(adherents.map((a) => a.id));
    const list = userAdherents.filter((u) => !alreadyIds.has(u.id));
    return [...list].sort((a, b) =>
      `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, "fr")
    );
  }, [adherents, userAdherents]);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const getStatusTag = () => {
    if (userRegisteredAdherents.length > 0) {
      const anyValidated = userRegisteredAdherents.some(
        (a) => a.AdherentEvent?.is_validate
      );
      return anyValidated ? (
        <div className="absolute top-0 left-0 bg-green-500 text-white px-4 py-2 text-sm font-bold">
          INSCRIT
        </div>
      ) : (
        <div className="absolute top-0 left-0 bg-yellow-500 text-black px-4 py-2 text-sm font-bold">
          PRÉ-INSCRIT
        </div>
      );
    }
    return null;
  };

  // ✅ Badge “Inscriptions closes”
  const getRegisterClosedTag = () => {
    if (!canRegister) {
      return (
        <div className="absolute top-0 right-0 bg-red-600 text-white px-4 py-2 text-sm font-bold">
          INSCRIPTIONS CLOSES
        </div>
      );
    }
    return null;
  };

  const handleInfo = () => setModalOpen(true);

  const handleFavoriteToggle = async () => {
    try {
      if (isFavorite) await deleteUserEvent(userId, event.id);
      else await addUserEvent(userId, event.id);
      setIsFavorite((s) => !s);
    } catch {
      toast.error("Impossible de mettre à jour les favoris.");
    }
  };

  // ✅ Si inscriptions closes, on force l’ouverture du panneau sur “Désinscrire”
  const openPanelFor = (mode) => {
    const safeMode = mode === "register" && !canRegister ? "unregister" : mode;
    setPanelMode(safeMode);
    setSelectedAdherent("");
    setPanelOpen(true);

    if (mode === "register" && !canRegister) {
      toast.info("Inscriptions closes pour cet événement. Désinscription possible.");
    }
  };

  const confirmRegistration = async () => {
    // ✅ Bloque l’inscription si can_register = 0
    if (!canRegister) {
      toast.info("Inscriptions closes pour cet événement.");
      return;
    }
    if (!selectedAdherent) return toast.error("Aucun adhérent sélectionné.");

    try {
      await registerAdherent(event.id, selectedAdherent);
      const adh = userAdherents.find(
        (a) => a.id === parseInt(selectedAdherent, 10)
      );
      setAdherents((prev) => [...prev, adh]);
      setAdherentsCount((n) => n + 1);

      const message = `Inscription réussie de ${adh.nom} ${
        adh.prenom
      } sur l'événement: ${event.titre} du ${new Date(
        event.start
      ).toLocaleDateString("fr-FR")}`;
      const recipients = await getAdminsAndInstructorsIds();
      await Promise.all(
        recipients.map((r) =>
          sendNotification(message, r.id, user.token).catch(() => {})
        )
      );

      toast.success("Inscription réussie !");
      setPanelOpen(false);
    } catch {
      toast.error("Erreur lors de l'inscription.");
    }
  };

  const confirmUnregister = async () => {
    if (!selectedAdherent)
      return toast.error("Veuillez sélectionner un adhérent.");
    try {
      await unregisterAdherent(selectedAdherent, event.id);
      setAdherents((prev) =>
        prev.filter((a) => a.id !== parseInt(selectedAdherent, 10))
      );
      setAdherentsCount((n) => Math.max(0, n - 1));

      const adh =
        adherents.find((a) => a.id === parseInt(selectedAdherent, 10)) ||
        userAdherents.find((a) => a.id === parseInt(selectedAdherent, 10));

      const message = `Désinscription réussie de ${adh?.nom ?? ""} ${
        adh?.prenom ?? ""
      } de l'événement: ${event.titre} du ${new Date(
        event.start
      ).toLocaleDateString("fr-FR")}`;
      const recipients = await getAdminsAndInstructorsIds();
      await Promise.all(
        recipients.map((r) =>
          sendNotification(message, r.id, user.token).catch(() => {})
        )
      );

      toast.success("Désinscription réussie !");
      setPanelOpen(false);
    } catch {
      toast.error("Erreur lors de la désinscription.");
    }
  };

  // ✅ Le guard APRÈS les hooks
  if (!event.is_active) return null;

  return (
    <div
      className={`${eventBgClass} border-slate-300 dark:border-slate-600 border-2 dark:bg-slate-500 shadow-md rounded-lg overflow-hidden flex flex-col min-h-[400px] font-satoshi relative`}
    >
      {getStatusTag()}
      {getRegisterClosedTag()}

      <img
        src={imgSrc}
        alt={event.titre}
        loading="lazy"
        className="w-full h-64 object-cover flex-shrink-0"
        onError={(e) => (e.currentTarget.src = "/img/no-image.jpg")}
      />

      <div className="flex flex-col flex-grow p-4 justify-between">
        <div>
          <h3 className="text-normal font-bold text-gray-600 dark:text-slate-100">
            {event.titre ?? "(Sans titre)"}
          </h3>
          <p className="text-sm text-gray-600 dark:text-slate-300">
            {event.description}
          </p>

          <div className="mt-2">
            <div className="flex items-center text-gray-700 text-sm dark:text-slate-300">
              <FaCalendarAlt className="mr-2" />
              {formatDate(event.start)}
              {event.end ? ` - ${formatDate(event.end)}` : ""}
            </div>
            <div className="flex items-center text-gray-700 text-sm mt-1 dark:text-slate-300">
              <FaMapMarkerAlt className="mr-2" />
              {event.lieu_event || "Lieu à préciser"}
            </div>
            <div className="flex items-center text-gray-700 text-sm mt-1 dark:text-slate-300">
              <FaUser className="mr-2" /> Inscrits : {adherentsCount}
            </div>

            {/* ✅ Ligne “Inscriptions closes” (en plus du badge) */}
            {!canRegister && (
              <div className="mt-2 text-sm font-semibold text-red-700 dark:text-red-300">
                Inscriptions closes
              </div>
            )}

            {event.agenda_url && (
              <a
                href={event.agenda_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-black hover:text-primary dark:text-slate-300 dark:hover:text-primary text-sm mt-1"
              >
                <FaInfoCircle className="mr-2" />
                Ajouter à votre agenda
              </a>
            )}
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mt-4">
          <Button
            text={canRegister ? "S'inscrire ou Désinscrire" : "Désinscription"}
            onClick={() => openPanelFor(canRegister ? "register" : "unregister")}
            icon={FaCalendarCheck}
            className="w-sm gap-2 text-xs sm:text-sm"
          />
          <Button
            text=""
            onClick={() => setModalOpen(true)}
            icon={FaInfoCircle}
            className="w-sm gap-2 text-sm"
          />
          <Button
            text=""
            onClick={handleFavoriteToggle}
            icon={isFavorite ? FaHeart : FaRegHeart}
            className="w-sm gap-2 text-sm"
          />
        </div>

        {panelOpen && (
          <div className="mt-4 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 bg-white/90 dark:bg-slate-900/70">
            <div className="px-4 py-3 bg-slate-50/80 dark:bg-slate-800/70 flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Gérer l’inscription
                </span>
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  {event.titre} — {formatDate(event.start)}
                </span>

                {!canRegister && (
                  <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200 ring-1 ring-red-200/60 dark:ring-red-800/60">
                    Inscriptions closes
                  </span>
                )}
              </div>

              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="text-slate-600 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-700 rounded-md px-2 py-1 text-sm"
              >
                Fermer
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="inline-flex rounded-lg ring-1 ring-slate-300 dark:ring-slate-700 overflow-hidden">
                <button
                  type="button"
                  disabled={!canRegister}
                  title={!canRegister ? "Inscriptions closes" : "Inscrire"}
                  onClick={() => {
                    if (!canRegister) return;
                    setPanelMode("register");
                    setSelectedAdherent("");
                  }}
                  className={`px-3 py-1.5 text-sm transition ${
                    panelMode === "register"
                      ? "bg-violet-600 text-white"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                  } ${!canRegister ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Inscrire
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setPanelMode("unregister");
                    setSelectedAdherent("");
                  }}
                  className={`px-3 py-1.5 text-sm ${
                    panelMode === "unregister"
                      ? "bg-violet-600 text-white"
                      : "bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
                  }`}
                >
                  Désinscrire
                </button>
              </div>

              {panelMode === "register" ? (
                <div>
                  {!canRegister ? (
                    <p className="text-sm text-red-700 dark:text-red-300">
                      Inscriptions closes pour cet événement. Vous pouvez uniquement vous désinscrire.
                    </p>
                  ) : (
                    <>
                      <label className="block text-sm font-medium mb-1 text-slate-800 dark:text-slate-200">
                        Choisissez un adhérent à inscrire
                      </label>
                      <select
                        className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                        value={selectedAdherent}
                        onChange={(e) => setSelectedAdherent(e.target.value)}
                      >
                        <option value="">— Sélectionner —</option>
                        {eligibleForRegister.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.nom} {a.prenom}
                          </option>
                        ))}
                      </select>
                      {eligibleForRegister.length === 0 && (
                        <p className="text-xs mt-2 text-slate-500 dark:text-slate-400">
                          Aucun adhérent disponible à inscrire.
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-1 text-slate-800 dark:text-slate-200">
                    Choisissez un adhérent à désinscrire
                  </label>
                  <select
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    value={selectedAdherent}
                    onChange={(e) => setSelectedAdherent(e.target.value)}
                  >
                    <option value="">— Sélectionner —</option>
                    {eligibleForUnregister.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nom} {a.prenom}
                      </option>
                    ))}
                  </select>
                  {eligibleForUnregister.length === 0 && (
                    <p className="text-xs mt-2 text-slate-500 dark:text-slate-400">
                      Aucun adhérent éligible à la désinscription.
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPanelOpen(false)}
                  className="inline-flex items-center rounded-lg px-3 py-2 text-sm ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                >
                  Annuler
                </button>

                {panelMode === "register" ? (
                  <Button
                    text="Confirmer l’inscription"
                    onClick={confirmRegistration}
                    className="!rounded-lg"
                    disabled={!selectedAdherent || !canRegister}
                  />
                ) : (
                  <Button
                    text="Confirmer la désinscription"
                    onClick={confirmUnregister}
                    className="!rounded-lg"
                    disabled={!selectedAdherent}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <EventDetailsModalEnseignants
        event={event}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        adherents={adherents}
        refreshList={() => {}}
      />

      <ToastContainer />
    </div>
  );
};

export default EventCardUser;